import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Col,
  Tag,
  InputNumber,
  Typography,
  Select,
  Tabs,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../../api';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
const { Title } = Typography;
const { Option } = Select;

const Materials = () => {
  const location = useLocation();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [stockRange, setStockRange] = useState('');
  const [searchText, setSearchText] = useState('');
  
  // State for stock history modal
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  // State for stock adjustment modal
  const [isAdjustModalVisible, setIsAdjustModalVisible] = useState(false);
  const [adjustingRecord, setAdjustingRecord] = useState(null);
  const [adjustForm] = Form.useForm();

  const fetchMaterials = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const requestParams = {
        page: params.page,
        limit: params.pageSize,
        search: params.search,
      };
      
      // 如果不是"全部"tab，添加分类过滤
      if (params.categoryId) {
        requestParams.category_id = params.categoryId;
      }
      
      // 添加库存范围过滤
      if (params.minStock !== null && params.minStock !== undefined) {
        requestParams.min_stock = params.minStock;
      }
      if (params.maxStock !== null && params.maxStock !== undefined) {
        requestParams.max_stock = params.maxStock;
      }
      
      const response = await api.get('/v1/materials', { params: requestParams });
      setMaterials(response.data.data);
      setPagination(prev => ({
        ...prev,
        current: params.page,
        total: response.data.total,
        pageSize: params.pageSize,
      }));
    } catch (error) {
      message.error('获取物料列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (value) => {
    const categoryId = activeTab === 'all' ? null : activeTab;
    fetchMaterials({
      page: 1,
      pageSize: pagination.pageSize,
      search: value,
      categoryId,
      minStock: null,
      maxStock: null,
    });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    const categoryId = key === 'all' ? null : key;
    const { minStock, maxStock } = parseStockRange(stockRange);
    fetchMaterials({
      page: 1,
      pageSize: pagination.pageSize,
      search: searchText,
      categoryId,
      minStock,
      maxStock,
    });
  };

  const parseStockRange = (range) => {
    if (!range || !range.trim()) {
      return { minStock: null, maxStock: null };
    }
    // 移除所有空格后再分割
    const cleanRange = range.replace(/\s+/g, '');
    const parts = cleanRange.split('-');
    if (parts.length === 2) {
      const min = parts[0] ? parseFloat(parts[0]) : null;
      const max = parts[1] ? parseFloat(parts[1]) : null;
      return { 
        minStock: !isNaN(min) ? min : null, 
        maxStock: !isNaN(max) ? max : null 
      };
    }
    return { minStock: null, maxStock: null };
  };

  const handleStockFilter = () => {
    const categoryId = activeTab === 'all' ? null : activeTab;
    const { minStock, maxStock } = parseStockRange(stockRange);
    fetchMaterials({
      page: 1,
      pageSize: pagination.pageSize,
      search: searchText,
      categoryId,
      minStock,
      maxStock,
    });
  };

  const handleResetStockFilter = () => {
    setStockRange('');
    setSearchText('');
    const categoryId = activeTab === 'all' ? null : activeTab;
    fetchMaterials({
      page: 1,
      pageSize: pagination.pageSize,
      search: '',
      categoryId,
      minStock: null,
      maxStock: null,
    });
  };

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/v1/categories', { params: { type: 1, pageSize: -1 } });
      setCategories(response.data.data);
    } catch (error) {
      message.error('获取分类列表失败');
    }
  }, []);

  useEffect(() => {
    // 检查是否从仪表盘传入了库存范围筛选
    if (location.state?.stockRange) {
      setStockRange(location.state.stockRange);
      const { minStock: min, maxStock: max } = parseStockRange(location.state.stockRange);
      fetchMaterials({ page: 1, pageSize: 10, search: '', minStock: min, maxStock: max });
    } else {
      fetchMaterials({ page: 1, pageSize: 10, search: '' });
    }
    fetchCategories();
  }, [fetchMaterials, fetchCategories, location.state]);

  const handleTableChange = (newPagination) => {
    const categoryId = activeTab === 'all' ? null : activeTab;
    const { minStock, maxStock } = parseStockRange(stockRange);
    fetchMaterials({
      page: newPagination.current,
      pageSize: newPagination.pageSize,
      categoryId,
      minStock,
      maxStock,
    });
  };

  const showModal = (record = null) => {
    setEditingRecord(record);
    form.setFieldsValue(record || { name: '', specification: '', package_unit: '', unit: '', category_id: undefined });
    setIsModalVisible(true);
  };
  
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingRecord) {
        await api.put(`/v1/materials/${editingRecord.id}`, values);
        message.success('物料更新成功');
      } else {
        await api.post('/v1/materials', values);
        message.success('物料创建成功');
      }
      handleCancel();
      const categoryId = activeTab === 'all' ? null : activeTab;
      fetchMaterials({ page: pagination.current, pageSize: pagination.pageSize, categoryId });
    } catch (errorInfo) {
      const errorMsg = errorInfo.response?.data?.error || '操作失败';
      message.error(errorMsg);
    }
  };
  
  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/materials/${id}`);
      message.success('物料删除成功');
      const categoryId = activeTab === 'all' ? null : activeTab;
      fetchMaterials({ page: pagination.current, pageSize: pagination.pageSize, categoryId });
    } catch (error) {
      message.error('删除失败');
    }
  };

  // --- History Modal Handlers ---
  const showHistoryModal = (record) => {
    setSelectedMaterial(record);
    setIsHistoryModalVisible(true);
    fetchHistory(record.id, 1, 20);
  };

  const fetchHistory = useCallback(async (materialId, page, pageSize) => {
    if (!materialId) return;
    setHistoryLoading(true);
    try {
      const response = await api.get(`/v1/materials/${materialId}/stock-history`, {
        params: { page, limit: pageSize },
      });
      const { data, total, page: currentPage, limit: currentLimit } = response.data;
      setHistoryData(data);
      setHistoryPagination({
        current: currentPage,
        pageSize: currentLimit,
        total: total,
      });
    } catch (error) {
      message.error('获取库存记录失败');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleHistoryModalCancel = () => {
    setIsHistoryModalVisible(false);
    setSelectedMaterial(null);
    setHistoryData([]);
    setHistoryPagination({ current: 1, pageSize: 5, total: 0 });
  };

  const handleHistoryTableChange = (newPagination) => {
    fetchHistory(selectedMaterial.id, newPagination.current, newPagination.pageSize);
  };

  // --- Adjust Stock Modal Handlers ---
  const showAdjustModal = (record) => {
    setAdjustingRecord(record);
    adjustForm.setFieldsValue({ new_quantity: record.current_stock });
    setIsAdjustModalVisible(true);
  };
  
  const handleAdjustCancel = () => {
    setIsAdjustModalVisible(false);
    setAdjustingRecord(null);
    adjustForm.resetFields();
  };

  const handleAdjustOk = async () => {
    try {
      const values = await adjustForm.validateFields();
      await api.post(`/v1/materials/${adjustingRecord.id}/adjust-stock`, values);
      message.success('库存调整成功');
      handleAdjustCancel();
      const categoryId = activeTab === 'all' ? null : activeTab;
      fetchMaterials({ page: pagination.current, pageSize: pagination.pageSize, categoryId });
    } catch (errorInfo) {
        const errorMsg = errorInfo.response?.data?.error || '操作失败';
        message.error(errorMsg);
    }
  };


  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '物料名称', dataIndex: 'name', key: 'name' },
    { 
      title: '当前库存', 
      dataIndex: 'current_stock', 
      key: 'current_stock', 
      render: (stock, record) => {
        let packageCount = 0;
        if (record.specification) {
          const rawCount = stock / parseFloat(record.specification);
          // 保留2位小数
          const rounded = Math.floor(rawCount * 100) / 100;
          packageCount = rounded;
        }
        return (
          <span>
            {stock}{record.unit}
            {record.specification && record.package_unit && (
              <span style={{ color: '#1890ff', marginLeft: 8 }}>
                ({packageCount}{record.package_unit})
              </span>
            )}
          </span>
        );
      }
    },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at' },
    {
      title: '操作',
      key: 'action',
      align: 'left',
      render: (_, record) => (
        <Space size="small" style={{ marginLeft: -8 }}>
          <Button type="link" size="small" onClick={() => showModal(record)}>编辑</Button>
          <Button type="link" size="small" onClick={() => showAdjustModal(record)}>调整库存</Button>
          <Button type="link" size="small" onClick={() => showHistoryModal(record)}>库存变更记录</Button>
          <Popconfirm
            title="确定删除此物料吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const historyColumns = [
    { title: '变更类型', dataIndex: 'transaction_type', key: 'transaction_type', render: type => type === 'in' ? <Tag color="green">入库</Tag> : <Tag color="red">出库</Tag> },
    { 
      title: '业务类型', 
      dataIndex: 'business_type', 
      key: 'business_type',
      render: (type) => {
          const businessTypeMap = {
              adjustment: '库存调整',
              purchase: '采购入库',
              production: '生产出库'
          };
          return businessTypeMap[type] || type;
      }
    },
    { title: '变更前数量', dataIndex: 'before_quantity', key: 'before_quantity' },
    { 
      title: '变更数量', 
      key: 'change_quantity',
      render: (_, record) => {
        const change = record.after_quantity - record.before_quantity;
        const color = change > 0 ? '#52c41a' : '#ff4d4f';
        const prefix = change > 0 ? '+' : '';
        return <span style={{ color }}>{prefix}{change}</span>;
      }
    },
    { title: '变更后数量', dataIndex: 'after_quantity', key: 'after_quantity' },
    { title: '关联单据/说明', dataIndex: 'reference_no', key: 'reference_no' },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : 'N/A' },
  ];
  
  // 构建Tab items
  const tabItems = [
    {
      key: 'all',
      label: '全部',
    },
    ...categories.map(cat => ({
      key: String(cat.id),
      label: cat.name,
    })),
  ];
  
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>物料管理</Title>
        <Space>
          <Input
            placeholder="搜索物料名称"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClear={() => {
              setSearchText('');
              const categoryId = activeTab === 'all' ? null : activeTab;
              const { minStock, maxStock } = parseStockRange(stockRange);
              fetchMaterials({
                page: 1,
                pageSize: pagination.pageSize,
                search: '',
                categoryId,
                minStock,
                maxStock,
              });
            }}
            style={{ width: 200 }}
            allowClear
          />
          <Input
            placeholder="库存范围 (例如: 10-20)"
            value={stockRange}
            onChange={(e) => setStockRange(e.target.value)}
            onClear={() => {
              setStockRange('');
              const categoryId = activeTab === 'all' ? null : activeTab;
              fetchMaterials({
                page: 1,
                pageSize: pagination.pageSize,
                search: searchText,
                categoryId,
                minStock: null,
                maxStock: null,
              });
            }}
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" onClick={handleStockFilter}>筛选</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            新增物料
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />

      <Table
        columns={columns}
        dataSource={materials}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        size="small"
        bordered
      />

      <Modal
        title={editingRecord ? '编辑物料' : '新增物料'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnHidden // Replaced destroyOnClose with destroyOnHidden
      >
        <Form form={form} layout="vertical" name="material_form">
          <Form.Item name="name" label="物料名称" rules={[{ required: true, message: '请输入物料名称!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="specification" label="每包装含量" rules={[{ required: true, message: '请输入每包装含量!' }, { pattern: /^\d+$/, message: '每包装含量必须为纯数字!' }]}>
            <Input placeholder="请输入纯数字" />
          </Form.Item>
          <Form.Item name="package_unit" label="包装单位" rules={[{ required: true, message: '请选择包装单位!' }]}>
            <Select placeholder="请选择包装单位" allowClear>
              <Option value="袋">袋</Option>
              <Option value="箱">箱</Option>
              <Option value="桶">桶</Option>
              <Option value="根">根</Option>
              <Option value="个">个</Option>
              <Option value="把">把</Option>
              <Option value="卷">卷</Option>
            </Select>
          </Form.Item>
          <Form.Item name="category_id" label="分类">
            <Select placeholder="请选择分类" allowClear>
              {categories.map(cat => <Option key={cat.id} value={cat.id}>{cat.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="unit" label="库存单位" rules={[{ required: true, message: '请输入库存单位!' }]}>
            <Input placeholder="例如：KG、L、个" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`库存记录: ${selectedMaterial?.name || ''}`}
        open={isHistoryModalVisible}
        onCancel={handleHistoryModalCancel}
        footer={[ <Button key="close" onClick={handleHistoryModalCancel}>关闭</Button> ]}
        width={800}
      >
        <Table
          columns={historyColumns}
          dataSource={historyData}
          rowKey="id"
          pagination={historyPagination}
          loading={historyLoading}
          onChange={handleHistoryTableChange}
          size="small"
          bordered
        />
      </Modal>
      
      <Modal
        title={`调整库存: ${adjustingRecord?.name || ''}`}
        open={isAdjustModalVisible}
        onOk={handleAdjustOk}
        onCancel={handleAdjustCancel}
        destroyOnClose
      >
        <Form form={adjustForm} layout="vertical" name="adjust_stock_form">
            <Form.Item label="当前库存">
                <Input value={adjustingRecord?.current_stock} disabled />
            </Form.Item>
            <Form.Item
                name="new_quantity"
                label="调整后库存"
                rules={[{ required: true, message: '请输入调整后的库存数量!' }]}
            >
                <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
                name="note"
                label="备注"
            >
                <Input.TextArea rows={2} placeholder="例如：盘点调整" />
            </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Materials;